import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  ChevronLeft,
  ChevronRight,
  Edit,
  HeartPulse,
  Loader2,
  Plus,
  Search,
  Trash2,
  User,
  Users,
  X,
} from 'lucide-react';
import {
  useGetPersonsPaged,
  useCreatePerson,
  useUpdatePerson,
  useDeletePerson,
} from '../hooks/Person';
import { usePermissions } from '../hooks/usePermissions';
import { BasePerson, CreatePerson, UpdatePerson, CreatePersonHealthProfile } from '../dtos/Persons/index';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const PAGE_SIZE_OPTIONS = [5, 10, 20, 50];

const personSchema = z.object({
  firstName: z.string().min(1, 'Prenumele este obligatoriu'),
  lastName: z.string().min(1, 'Numele este obligatoriu'),
  email: z.string().email('Email invalid').optional().or(z.literal('')),
  phone: z.string().optional(),
  cnp: z.string().optional(),
  age: z.coerce.number().optional(),
  gender: z.string().optional(),
  birthDate: z.string().optional(),
  country: z.string().optional(),
  city: z.string().optional(),
  address: z.string().optional(),
});

type PersonFormValues = z.infer<typeof personSchema>;

interface HealthProfileFormValues {
  enabled: boolean;
  isSmoker: boolean;
  isFormerSmoker: boolean;
  hasDiabetes: boolean;
  hasHypertension: boolean;
  hasDyslipidemia: boolean;
  bodyMassIndex?: number;
  systolicBloodPressure?: number;
  familyHistoryCardiovascularDisease: boolean;
}

const defaultHealthProfile: HealthProfileFormValues = {
  enabled: false,
  isSmoker: false,
  isFormerSmoker: false,
  hasDiabetes: false,
  hasHypertension: false,
  hasDyslipidemia: false,
  bodyMassIndex: undefined,
  systolicBloodPressure: undefined,
  familyHistoryCardiovascularDisease: false,
};

// Health Profile Form Component
function HealthProfileForm({
  values,
  onChange,
  idPrefix,
}: {
  values: HealthProfileFormValues;
  onChange: (values: HealthProfileFormValues) => void;
  idPrefix: string;
}) {
  const updateField = <K extends keyof HealthProfileFormValues>(
    field: K,
    value: HealthProfileFormValues[K]
  ) => {
    onChange({ ...values, [field]: value });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center space-x-2 p-3 bg-muted/50 rounded-lg">
        <Checkbox
          id={`${idPrefix}-hp-enabled`}
          checked={values.enabled}
          onCheckedChange={(checked) => updateField('enabled', !!checked)}
        />
        <Label htmlFor={`${idPrefix}-hp-enabled`} className="text-sm font-medium cursor-pointer">
          Adaugă Profil de Sănătate
        </Label>
      </div>

      {values.enabled && (
        <div className="space-y-4 p-4 border rounded-lg">
          <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground mb-2">
            <HeartPulse className="h-4 w-4" />
            Informații Profil Sănătate
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor={`${idPrefix}-bmi`}>IMC (Body Mass Index)</Label>
              <Input
                id={`${idPrefix}-bmi`}
                type="number"
                step="0.1"
                value={values.bodyMassIndex ?? ''}
                onChange={(e) =>
                  updateField('bodyMassIndex', e.target.value ? parseFloat(e.target.value) : undefined)
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor={`${idPrefix}-sbp`}>Tensiune Sistolică (mmHg)</Label>
              <Input
                id={`${idPrefix}-sbp`}
                type="number"
                value={values.systolicBloodPressure ?? ''}
                onChange={(e) =>
                  updateField('systolicBloodPressure', e.target.value ? parseInt(e.target.value) : undefined)
                }
              />
            </div>
          </div>

          <div className="space-y-3">
            <Label>Condiții Medicale</Label>
            <div className="grid grid-cols-2 gap-3">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id={`${idPrefix}-smoker`}
                  checked={values.isSmoker}
                  onCheckedChange={(checked) => updateField('isSmoker', !!checked)}
                />
                <Label htmlFor={`${idPrefix}-smoker`} className="text-sm font-normal">
                  Fumător
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id={`${idPrefix}-formerSmoker`}
                  checked={values.isFormerSmoker}
                  onCheckedChange={(checked) => updateField('isFormerSmoker', !!checked)}
                />
                <Label htmlFor={`${idPrefix}-formerSmoker`} className="text-sm font-normal">
                  Fost Fumător
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id={`${idPrefix}-diabetes`}
                  checked={values.hasDiabetes}
                  onCheckedChange={(checked) => updateField('hasDiabetes', !!checked)}
                />
                <Label htmlFor={`${idPrefix}-diabetes`} className="text-sm font-normal">
                  Diabet
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id={`${idPrefix}-hypertension`}
                  checked={values.hasHypertension}
                  onCheckedChange={(checked) => updateField('hasHypertension', !!checked)}
                />
                <Label htmlFor={`${idPrefix}-hypertension`} className="text-sm font-normal">
                  Hipertensiune
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id={`${idPrefix}-dyslipidemia`}
                  checked={values.hasDyslipidemia}
                  onCheckedChange={(checked) => updateField('hasDyslipidemia', !!checked)}
                />
                <Label htmlFor={`${idPrefix}-dyslipidemia`} className="text-sm font-normal">
                  Dislipidemie
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id={`${idPrefix}-cvHistory`}
                  checked={values.familyHistoryCardiovascularDisease}
                  onCheckedChange={(checked) =>
                    updateField('familyHistoryCardiovascularDisease', !!checked)
                  }
                />
                <Label htmlFor={`${idPrefix}-cvHistory`} className="text-sm font-normal">
                  Istoric Cardiovascular Familial
                </Label>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Persons() {
  const { canCreate, canUpdate, canDelete } = usePermissions();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchInput, setSearchInput] = useState('');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<BasePerson | null>(null);

  // Health profile state for create and edit
  const [createHealthProfile, setCreateHealthProfile] = useState<HealthProfileFormValues>(defaultHealthProfile);
  const [editHealthProfile, setEditHealthProfile] = useState<HealthProfileFormValues>(defaultHealthProfile);

  const page = parseInt(searchParams.get('page') || '1', 10);
  const pageSize = parseInt(searchParams.get('size') || '10', 10);
  const searchTerm = searchParams.get('search') || '';

  useEffect(() => {
    setSearchInput(searchTerm);
  }, [searchTerm]);

  const { data: response, isLoading } = useGetPersonsPaged(page, pageSize, searchTerm || undefined);
  const createMutation = useCreatePerson();
  const updateMutation = useUpdatePerson();
  const deleteMutation = useDeletePerson();

  const pagedData = response?.isSuccess ? response.data : null;
  const items = pagedData?.items || [];
  const totalCount = pagedData?.totalCount || 0;
  const totalPages = pagedData?.totalPages || 1;

  const updateParams = (updates: { page?: number; size?: number; search?: string }) => {
    const newParams = new URLSearchParams(searchParams);
    if (updates.page !== undefined) {
      if (updates.page === 1) newParams.delete('page');
      else newParams.set('page', updates.page.toString());
    }
    if (updates.size !== undefined) {
      if (updates.size === 10) newParams.delete('size');
      else newParams.set('size', updates.size.toString());
    }
    if (updates.search !== undefined) {
      if (updates.search === '') newParams.delete('search');
      else newParams.set('search', updates.search);
      newParams.delete('page');
    }
    setSearchParams(newParams);
  };

  const handleSearch = () => updateParams({ search: searchInput });
  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };
  const clearSearch = () => {
    setSearchInput('');
    updateParams({ search: '' });
  };

  const createForm = useForm<PersonFormValues>({
    resolver: zodResolver(personSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      cnp: '',
      age: undefined,
      gender: '',
      birthDate: '',
      country: '',
      city: '',
      address: '',
    },
  });

  const editForm = useForm<PersonFormValues>({
    resolver: zodResolver(personSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      cnp: '',
      age: undefined,
      gender: '',
      birthDate: '',
      country: '',
      city: '',
      address: '',
    },
  });

  const buildHealthProfileData = (hp: HealthProfileFormValues): CreatePersonHealthProfile | undefined => {
    if (!hp.enabled) return undefined;
    return {
      isSmoker: hp.isSmoker,
      isFormerSmoker: hp.isFormerSmoker,
      hasDiabetes: hp.hasDiabetes,
      hasHypertension: hp.hasHypertension,
      hasDyslipidemia: hp.hasDyslipidemia,
      bodyMassIndex: hp.bodyMassIndex,
      systolicBloodPressure: hp.systolicBloodPressure,
      familyHistoryCardiovascularDisease: hp.familyHistoryCardiovascularDisease,
    };
  };

  const handleCreate = async (values: PersonFormValues) => {
    const data: CreatePerson = {
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email || undefined,
      phone: values.phone || undefined,
      cnp: values.cnp || undefined,
      age: values.age,
      gender: values.gender || undefined,
      birthDate: values.birthDate || undefined,
      country: values.country || undefined,
      city: values.city || undefined,
      address: values.address || undefined,
      healthProfile: buildHealthProfileData(createHealthProfile),
    };
    const res = await createMutation.mutateAsync(data);
    if (res.isSuccess) {
      setIsCreateDialogOpen(false);
      createForm.reset();
      setCreateHealthProfile(defaultHealthProfile);
    }
  };

  const handleEdit = async (values: PersonFormValues) => {
    if (!selectedItem) return;
    const data: Omit<UpdatePerson, 'id'> = {
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email || undefined,
      phone: values.phone || undefined,
      cnp: values.cnp || undefined,
      age: values.age,
      gender: values.gender || undefined,
      birthDate: values.birthDate || undefined,
      country: values.country || undefined,
      city: values.city || undefined,
      address: values.address || undefined,
      healthProfile: buildHealthProfileData(editHealthProfile),
    };
    const res = await updateMutation.mutateAsync({ id: selectedItem.id, data });
    if (res.isSuccess) {
      setIsEditDialogOpen(false);
      setSelectedItem(null);
      editForm.reset();
      setEditHealthProfile(defaultHealthProfile);
    }
  };

  const handleDelete = async () => {
    if (!selectedItem) return;
    const res = await deleteMutation.mutateAsync(selectedItem.id);
    if (res.isSuccess) {
      setIsDeleteDialogOpen(false);
      setSelectedItem(null);
    }
  };

  const openCreateDialog = () => {
    createForm.reset();
    setCreateHealthProfile(defaultHealthProfile);
    setIsCreateDialogOpen(true);
  };

  const openEditDialog = (item: BasePerson) => {
    setSelectedItem(item);
    editForm.reset({
      firstName: item.firstName || '',
      lastName: item.lastName || '',
      email: item.email || '',
      phone: item.phone || '',
      cnp: item.cnp || '',
      age: item.age,
      gender: item.gender || '',
      birthDate: item.birthDate || '',
      country: item.country || '',
      city: item.city || '',
      address: item.address || '',
    });
    // Load existing health profile if present
    if (item.healthProfile) {
      setEditHealthProfile({
        enabled: true,
        isSmoker: item.healthProfile.isSmoker,
        isFormerSmoker: item.healthProfile.isFormerSmoker,
        hasDiabetes: item.healthProfile.hasDiabetes,
        hasHypertension: item.healthProfile.hasHypertension,
        hasDyslipidemia: item.healthProfile.hasDyslipidemia,
        bodyMassIndex: item.healthProfile.bodyMassIndex,
        systolicBloodPressure: item.healthProfile.systolicBloodPressure,
        familyHistoryCardiovascularDisease: item.healthProfile.familyHistoryCardiovascularDisease,
      });
    } else {
      setEditHealthProfile(defaultHealthProfile);
    }
    setIsEditDialogOpen(true);
  };

  const openDeleteDialog = (item: BasePerson) => {
    setSelectedItem(item);
    setIsDeleteDialogOpen(true);
  };

  // Render person form fields (shared between create and edit)
  const renderPersonFields = (form: typeof createForm | typeof editForm, prefix: string) => (
    <div className="grid gap-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor={`${prefix}-firstName`}>Prenume *</Label>
          <Input
            id={`${prefix}-firstName`}
            {...form.register('firstName')}
            className={form.formState.errors.firstName ? 'border-destructive' : ''}
          />
          {form.formState.errors.firstName && (
            <p className="text-sm text-destructive">{form.formState.errors.firstName.message}</p>
          )}
        </div>
        <div className="space-y-2">
          <Label htmlFor={`${prefix}-lastName`}>Nume *</Label>
          <Input
            id={`${prefix}-lastName`}
            {...form.register('lastName')}
            className={form.formState.errors.lastName ? 'border-destructive' : ''}
          />
          {form.formState.errors.lastName && (
            <p className="text-sm text-destructive">{form.formState.errors.lastName.message}</p>
          )}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor={`${prefix}-email`}>Email</Label>
          <Input id={`${prefix}-email`} type="email" {...form.register('email')} />
        </div>
        <div className="space-y-2">
          <Label htmlFor={`${prefix}-phone`}>Telefon</Label>
          <Input id={`${prefix}-phone`} {...form.register('phone')} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor={`${prefix}-cnp`}>CNP</Label>
          <Input id={`${prefix}-cnp`} {...form.register('cnp')} />
        </div>
        <div className="space-y-2">
          <Label htmlFor={`${prefix}-birthDate`}>Data Nașterii</Label>
          <Input id={`${prefix}-birthDate`} type="date" {...form.register('birthDate')} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor={`${prefix}-age`}>Vârstă</Label>
          <Input id={`${prefix}-age`} type="number" {...form.register('age')} />
        </div>
        <div className="space-y-2">
          <Label htmlFor={`${prefix}-gender`}>Gen</Label>
          <Select
            value={form.watch('gender') || ''}
            onValueChange={(value) => form.setValue('gender', value)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Selectați" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Masculin">Masculin</SelectItem>
              <SelectItem value="Feminin">Feminin</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor={`${prefix}-country`}>Țară</Label>
          <Input id={`${prefix}-country`} {...form.register('country')} />
        </div>
        <div className="space-y-2">
          <Label htmlFor={`${prefix}-city`}>Oraș</Label>
          <Input id={`${prefix}-city`} {...form.register('city')} />
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${prefix}-address`}>Adresă</Label>
        <Input id={`${prefix}-address`} {...form.register('address')} />
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Persoane</h1>
          <p className="text-muted-foreground mt-2">Gestionați persoanele din sistem.</p>
        </div>
        {canCreate() && (
          <Button onClick={openCreateDialog}>
            <Plus className="h-4 w-4 mr-2" />
            Adaugă Persoană
          </Button>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Lista Persoane
          </CardTitle>
          <CardDescription>{totalCount} persoane în total</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Caută persoane..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                className="pl-10"
              />
              {searchInput && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                  onClick={clearSearch}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
            <Button onClick={handleSearch} variant="secondary">
              <Search className="h-4 w-4 mr-2" />
              Caută
            </Button>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <Users className="h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground">
                {searchTerm ? 'Nu s-au găsit persoane.' : 'Nu există persoane.'}
              </p>
            </div>
          ) : (
            <>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>ID Intern</TableHead>
                      <TableHead>Nume</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Telefon</TableHead>
                      <TableHead>Vârstă</TableHead>
                      <TableHead>Gen</TableHead>
                      <TableHead>Profil Sănătate</TableHead>
                      <TableHead className="w-[100px]">Acțiuni</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell className="font-mono text-sm">
                          {item.personInternalId || '-'}
                        </TableCell>
                        <TableCell className="font-medium">
                          {item.firstName} {item.lastName}
                        </TableCell>
                        <TableCell>{item.email || '-'}</TableCell>
                        <TableCell>{item.phone || '-'}</TableCell>
                        <TableCell>{item.age ?? '-'}</TableCell>
                        <TableCell>
                          {item.gender ? (
                            <Badge variant="secondary">{item.gender}</Badge>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell>
                          {item.healthProfile ? (
                            <Badge variant="default" className="gap-1">
                              <HeartPulse className="h-3 w-3" />
                              Da
                            </Badge>
                          ) : (
                            <Badge variant="outline">Nu</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {(canUpdate() || canDelete()) && (
                            <div className="flex items-center gap-1">
                              {canUpdate() && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0"
                                  onClick={() => openEditDialog(item)}
                                  title="Editează"
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                              )}
                              {canDelete() && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                                  onClick={() => openDeleteDialog(item)}
                                  title="Șterge"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <div className="flex items-center justify-between mt-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Rânduri pe pagină:</span>
                  <Select
                    value={pageSize.toString()}
                    onValueChange={(value) => updateParams({ size: parseInt(value, 10), page: 1 })}
                  >
                    <SelectTrigger className="w-[70px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PAGE_SIZE_OPTIONS.map((size) => (
                        <SelectItem key={size} value={size.toString()}>
                          {size}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">
                    Pagina {page} din {totalPages}
                  </span>
                  <div className="flex gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => updateParams({ page: page - 1 })}
                      disabled={page <= 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => updateParams({ page: page + 1 })}
                      disabled={page >= totalPages}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Adaugă Persoană
            </DialogTitle>
            <DialogDescription>Completați informațiile pentru noua persoană.</DialogDescription>
          </DialogHeader>
          <form onSubmit={createForm.handleSubmit(handleCreate)}>
            <Tabs defaultValue="general" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="general">Date Personale</TabsTrigger>
                <TabsTrigger value="health">
                  Profil Sănătate
                  {createHealthProfile.enabled && (
                    <Badge variant="secondary" className="ml-2">
                      <HeartPulse className="h-3 w-3" />
                    </Badge>
                  )}
                </TabsTrigger>
              </TabsList>

              <TabsContent value="general" className="mt-4">
                {renderPersonFields(createForm, 'create')}
              </TabsContent>

              <TabsContent value="health" className="mt-4">
                <HealthProfileForm
                  values={createHealthProfile}
                  onChange={setCreateHealthProfile}
                  idPrefix="create"
                />
              </TabsContent>
            </Tabs>

            <DialogFooter className="mt-6">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setIsCreateDialogOpen(false);
                  createForm.reset();
                  setCreateHealthProfile(defaultHealthProfile);
                }}
              >
                Anulează
              </Button>
              <Button type="submit" disabled={createMutation.isPending}>
                {createMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Se creează...
                  </>
                ) : (
                  'Creează'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="h-5 w-5" />
              Editează Persoană
            </DialogTitle>
            <DialogDescription>Modificați informațiile persoanei.</DialogDescription>
          </DialogHeader>
          <form onSubmit={editForm.handleSubmit(handleEdit)}>
            <Tabs defaultValue="general" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="general">Date Personale</TabsTrigger>
                <TabsTrigger value="health">
                  Profil Sănătate
                  {editHealthProfile.enabled && (
                    <Badge variant="secondary" className="ml-2">
                      <HeartPulse className="h-3 w-3" />
                    </Badge>
                  )}
                </TabsTrigger>
              </TabsList>

              <TabsContent value="general" className="mt-4">
                {renderPersonFields(editForm, 'edit')}
              </TabsContent>

              <TabsContent value="health" className="mt-4">
                <HealthProfileForm
                  values={editHealthProfile}
                  onChange={setEditHealthProfile}
                  idPrefix="edit"
                />
              </TabsContent>
            </Tabs>

            <DialogFooter className="mt-6">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setIsEditDialogOpen(false);
                  setSelectedItem(null);
                  editForm.reset();
                  setEditHealthProfile(defaultHealthProfile);
                }}
              >
                Anulează
              </Button>
              <Button type="submit" disabled={updateMutation.isPending}>
                {updateMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Se salvează...
                  </>
                ) : (
                  'Salvează'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmați ștergerea</AlertDialogTitle>
            <AlertDialogDescription>
              Sunteți sigur că doriți să ștergeți persoana{' '}
              <strong>
                {selectedItem?.firstName} {selectedItem?.lastName}
              </strong>
              ?{' '}
              {selectedItem?.healthProfile && (
                <>Profilul de sănătate asociat va fi de asemenea șters. </>
              )}
              Această acțiune nu poate fi anulată.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setSelectedItem(null)}>Anulează</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Se șterge...
                </>
              ) : (
                'Șterge'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
